import {
   ApplicationConfig,
   isDevMode,
   provideZonelessChangeDetection,
   LOCALE_ID,
} from '@angular/core';
import {
   provideRouter,
   withComponentInputBinding,
   withPreloading,
} from '@angular/router';
import localeKh from '@angular/common/locales/km';
import { registerLocaleData } from '@angular/common';

import { routes } from './app.routes';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
   browserLocalPersistence,
   browserPopupRedirectResolver,
   connectAuthEmulator,
   getAuth,
   indexedDBLocalPersistence,
   initializeAuth,
   provideAuth,
} from '@angular/fire/auth';
import {
   connectFirestoreEmulator,
   getFirestore,
   initializeFirestore,
   persistentLocalCache,
   persistentMultipleTabManager,
   provideFirestore,
} from '@angular/fire/firestore';
import {
   connectStorageEmulator,
   getStorage,
   provideStorage,
} from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';

import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { SelectivePreloadingStrategy } from './custom-preloading.strategy';
import {
   initializeAppCheck,
   provideAppCheck,
   ReCaptchaV3Provider,
} from '@angular/fire/app-check';

registerLocaleData(localeKh);

// Add this to handle debug provider globally if needed,
// though AngularFire usually handles this via environment variables or provider config.
// For browser environments, you can set self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;

const useEmulators = environment.useEmulators;
const localIpAddress = environment.localIpAddress;
const appCheck = environment.appCheck;

export const appConfig: ApplicationConfig = {
   providers: [
      provideZonelessChangeDetection(),
      provideRouter(
         routes,
         withPreloading(SelectivePreloadingStrategy),
         withComponentInputBinding(),
         // View transitions removed: on iOS Safari the whole-page snapshot on
         // every navigation (made worse by backdrop-filter blur) caused slow,
         // "stacking" navigation. Per-component animations still provide motion.
      ),
      provideFirebaseApp(() => initializeApp(environment.firebase)),

      // App Check is initialized at boot (before the first Firestore/Auth
      // request) so the app stays compatible with App Check *enforcement*.
      // Reload speed is addressed via optimistic render from cache instead.
      provideAppCheck(() => {
         if (useEmulators) {
            return null as any;
         }

         if (appCheck.useDebugProvider) {
            (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
         }

         return initializeAppCheck(getApp(), {
            provider: new ReCaptchaV3Provider(appCheck.siteKey),
            isTokenAutoRefreshEnabled: true,
         });
      }),
      // Analytics is loaded lazily via AnalyticsService (keeps
      // @firebase/analytics out of the initial bundle). Automatic
      // screen_view / user-id tracking is intentionally not registered.
      provideAuth(() => {
         if (useEmulators) {
            const auth = getAuth();
            connectAuthEmulator(auth, `http://${localIpAddress}:9099`, {
               disableWarnings: true,
            });
            return auth;
         }
         return initializeAuth(getApp(), {
            persistence: [indexedDBLocalPersistence, browserLocalPersistence],
            popupRedirectResolver: browserPopupRedirectResolver,
         });
      }),
      provideFirestore(() => {
         if (useEmulators) {
            const firestore = getFirestore();
            connectFirestoreEmulator(firestore, localIpAddress, 8080);
            return firestore;
         } else {
            return initializeFirestore(getApp(), {
               localCache: persistentLocalCache({
                  tabManager: persistentMultipleTabManager(), // supports multiple tabs
               }),
            });
         }
      }),
      provideStorage(() => {
         const storage = getStorage();
         if (useEmulators) {
            connectStorageEmulator(storage, localIpAddress, 9199);
         }
         return storage;
      }),
      // Cloud Functions are loaded lazily via callFunction() in
      // services/lazy-firebase.ts (keeps @firebase/functions out of the
      // initial bundle). HrService keeps its own asia-southeast1 instance.
      provideAnimationsAsync(),
      provideServiceWorker('ngsw-worker.js', {
         enabled: !isDevMode(),
         registrationStrategy: 'registerWhenStable:30000',
      }),
      {
         provide: MAT_DIALOG_DEFAULT_OPTIONS,
         useValue: { position: { top: '20px' } },
      },
      provideNativeDateAdapter(),
      provideHttpClient(withFetch()),
      { provide: LOCALE_ID, useValue: 'km-KH' },
   ],
};
